import { defineMeta } from '@/registry/types';

export const tableCellsMeta = defineMeta({
  name: 'TableCells',
  level: 'molecule',
  category: 'data-display',
  classification: 'custom',
  description:
    'Row anatomy for DataTable: title, status, metric, meta, chips and actions cells. Use these inside a column `Cell` renderer instead of building cells from Box + Typography.',
  baseLibrary: 'custom',
  importPath: '@/organisms/Table',
  requiredProps: [],
  optionalProps: ['title', 'description', 'icon', 'meta', 'badge', 'label', 'tone', 'value', 'trend', 'items', 'max'],
  supportedStates: ['default', 'truncated', 'overflowing', 'muted', 'hover-revealed'],
  designTokens: ['colors', 'radius', 'spacing', 'typography'],
  accessibility: [
    'TableStatusCell conveys state by colour AND text - never drop the label.',
    'TableMetricCell requires `trendLabel` whenever `trend` is supplied.',
    'TableActionsCell stays keyboard-reachable when visually hidden until hover.',
  ],
  usageExamples: [
    "{ accessorKey: 'name', header: 'Workflow', size: 280, Cell: ({ row }) => <TableTitleCell icon={<BoltIcon />} title={row.original.name} meta={row.original.version} badge={row.original.builtIn ? 'Built-in' : undefined} description={row.original.summary} /> }",
    "{ accessorKey: 'status', header: 'Status', size: 120, Cell: ({ row }) => <TableStatusCell tone={row.original.enabled ? 'active' : 'neutral'} label={row.original.enabled ? 'Active' : 'Disabled'} /> }",
    "{ accessorKey: 'volume', header: 'Volume 24h', size: 150, Cell: ({ row }) => <TableMetricCell trend={row.original.volume24h} trendLabel=\"Executions, last 24 hours\" value={formatCompact(total)} /> }",
  ],
  rules: [
    'Each cell applies its own variant: title subtitle2, description body1, meta/badge uses Chip variant="subtle" color="primary", status body1, metric subtitle2, chips use Chip variant="subtle" color="neutral".',
    'TableChipsCell and the TableTitleCell badge both use the design-system Chip atom internally — never hand-roll pill elements in a table cell.',
  ],
  antiPatterns: [
    'Do not rebuild the two-line title cell from Box + Typography - use TableTitleCell.',
    'Do not use a filled Chip for lifecycle state; state is a dot + label (TableStatusCell), chips are for facets.',
    'Do not hardcode hex for row text - `textColors.subtle` and `textColors.muted` exist for the secondary lines.',
    'Do not build tag chips from Typography + inline sx — use the Chip atom with the appropriate variant and color.',
  ],
  llmSafe: true,
  whenToUse: ['Any column in a DataTable that shows more than a bare string'],
  whenNotToUse: ['Outside a table - these are tuned to row rhythm, not general layout'],
});

export default tableCellsMeta;
